// Port of CallCamera + CallCameraChoice (ios/Sources/Calls/CallCameras.swift; Android twin).

export type CallCameraPosition = 'front' | 'back' | 'other';

export interface CallCamera {
  id: string;
  name: string;
  position: CallCameraPosition;
}

export const CallCameraChoice = {
  /** Where the pick is remembered (Apple's UserDefaults key, prefixed like every web key). */
  storageKey: 'satori.call_camera_id',

  /**
   * Which camera a call opens: the one the reader picked last time, while it is still plugged in;
   * otherwise the front one on a phone or tablet, and the first one on a desktop.
   */
  resolve(available: CallCamera[], saved: string | null, preferFront: boolean): CallCamera | null {
    if (saved) {
      const match = available.find((c) => c.id === saved);
      if (match) return match;
    }
    if (preferFront) {
      const front = available.find((c) => c.position === 'front');
      if (front) return front;
    }
    return available[0] ?? null;
  },

  /**
   * A camera that faces the person is shown as a mirror; a back camera is shown as it is
   * (mirroring a whiteboard makes it unreadable). On a desktop every camera faces the desk
   * (Apple's macOS rule), so only a camera KNOWN to face away is left unmirrored.
   */
  mirrorsSelfView(camera: CallCamera | null): boolean {
    return camera?.position !== 'back';
  },

  /** A browser camera's position, from what the device tells us (facingMode, else its label). */
  positionOf(facingMode: string | undefined, label: string): CallCameraPosition {
    if (facingMode === 'user') return 'front';
    if (facingMode === 'environment') return 'back';
    const l = label.toLowerCase();
    if (/\b(back|rear|trasera|environment)\b/.test(l)) return 'back';
    if (/\b(front|frontal|user|facetime)\b/.test(l)) return 'front';
    return 'other';
  },
};
